/**
 * Translates Shopify's cookie-consent banner and preferences dialog to Estonian.
 *
 * Shopify injects both into <body> after load, in the store's default language
 * (English for this shop), and the theme can't edit their text. Each node is
 * translated the moment it's added, before it can paint. Strings that aren't in
 * the map are left alone, so this is a no-op if the store language is switched
 * to Estonian in the admin.
 */

// Short and plain: what the cookies are for, and that the optional ones need a yes.
const BANNER_TEXT =
  'Kasutame küpsiseid, et leht töötaks ja et saaksime seda parandada. Reklaami- ja statistikaküpsised lubame ainult sinu nõusolekul. Loe lähemalt meie';

/** @type {Record<string, string>} */
const TRANSLATIONS = {
  'Cookie consent': 'Küpsised',
  'We and our partners, including Shopify, use cookies and other technologies to personalize your experience, show you ads, and perform analytics, and we will not use cookies or other technologies for these purposes unless you accept them. Learn more in our':
    BANNER_TEXT,
  'Privacy Policy': 'privaatsuspoliitikast',
  // The store language is Estonian, so Shopify's own Estonian wording arrives here: replaced as well.
  'Küpsiste nõusolek': 'Küpsised',
  'Meie ja meie partnerid, sealhulgas Shopify, kasutame küpsiseid ja muid tehnoloogiaid teie kasutajakogemuse isikupärastamiseks, reklaamide näitamiseks ja analüütikaks ning me ei kasuta küpsiseid ega muid tehnoloogiaid nendel eesmärkidel ilma teie nõusolekuta. Lisateavet leiate dokumendist':
    BANNER_TEXT,
  Privaatsuspoliitika: 'privaatsuspoliitikast',
  'Teie kontrollite oma andmeid': 'Sina kontrollid oma andmeid',
  'Vaadake lisateavet meie kasutatavate küpsiste kohta ja valige, milliseid küpsiseid lubada.':
    'Loe lähemalt, milliseid küpsiseid me kasutame, ja vali, milliseid lubad.',
  Kohustuslikud: 'Vajalikud',
  'Need küpsised on saidi õigeks toimimiseks vajalikud, sealhulgas sellised võimekused nagu sisselogimine ja üksuste ostukorvi lisamine.':
    'Need küpsised on vajalikud, et veebileht töötaks korrektselt, sh sisselogimiseks ja toodete ostukorvi lisamiseks.',
  'Need küpsised salvestavad üksikasju teie toimingute kohta, et isikupärastada teie järgmist veebisaidi külastust.':
    'Need küpsised salvestavad infot sinu tegevuste kohta, et isikupärastada sinu järgmist külastust.',
  'Neid küpsiseid kasutame meie ja meie partnerid, sealhulgas Shopify, et optimeerida turundussuhtlust ja näidata teile teistel veebisaitidel reklaame.':
    'Meie ja meie partnerid, sh Shopify, kasutame neid küpsiseid turundussõnumite optimeerimiseks ja reklaamide näitamiseks teistel veebilehtedel.',
  Analüütika: 'Statistika',
  'Need küpsised aitavad meil mõista, kuidas te saiti kasutate. Kasutame neid andmeid täiustamist vajavate valdkondade tuvastamiseks.':
    'Need küpsised aitavad meil mõista, kuidas sa lehega suhtled. Kasutame neid andmeid, et leida kohti, mida parandada.',
  'Salvesta minu valikud': 'Salvesta valikud',
  'Manage preferences': 'Halda eelistusi',
  Accept: 'Nõustun',
  Decline: 'Keeldun',
  'Close dialog': 'Sulge',
  'Cookie and privacy preferences': 'Küpsiste ja privaatsuse eelistused',
  'Accept all': 'Nõustu kõigiga',
  'Decline all': 'Keeldu kõigist',
  'Save my choices': 'Salvesta valikud',
  'You control your data': 'Sina kontrollid oma andmeid',
  'Learn more about the cookies we use, and choose which cookies to allow.':
    'Loe lähemalt, milliseid küpsiseid me kasutame, ja vali, milliseid lubad.',
  Required: 'Vajalikud',
  'These cookies are necessary for the site to function properly, including capabilities like logging in and adding items to the cart.':
    'Need küpsised on vajalikud, et veebileht töötaks korrektselt, sh sisselogimiseks ja toodete ostukorvi lisamiseks.',
  Personalization: 'Isikupärastamine',
  'These cookies store details about your actions to personalize your next visit to the website.':
    'Need küpsised salvestavad infot sinu tegevuste kohta, et isikupärastada sinu järgmist külastust.',
  Marketing: 'Turundus',
  'These cookies are used by us and our partners, including Shopify, to optimize marketing communications and show you ads on other websites.':
    'Meie ja meie partnerid, sh Shopify, kasutame neid küpsiseid turundussõnumite optimeerimiseks ja reklaamide näitamiseks teistel veebilehtedel.',
  Analytics: 'Statistika',
  'These cookies help us understand how you interact with the site. We use this data to identify areas to improve.':
    'Need küpsised aitavad meil mõista, kuidas sa lehega suhtled. Kasutame neid andmeid, et leida kohti, mida parandada.',
};

/** @param {Element} root */
function translate(root) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const text = node.nodeValue?.trim();
    const translated = text && TRANSLATIONS[text];
    if (translated) node.nodeValue = node.nodeValue.replace(text, translated);
  }

  for (const element of [root, ...root.querySelectorAll('[aria-label]')]) {
    const label = element.getAttribute('aria-label');
    if (label && TRANSLATIONS[label]) element.setAttribute('aria-label', TRANSLATIONS[label]);
  }
}

/** @param {Node} node */
function isConsentNode(node) {
  return node instanceof Element && (node.id.startsWith('shopify-pc') || node.className.toString().includes('shopify-pc'));
}

for (const node of document.body.children) {
  if (isConsentNode(node)) translate(/** @type {Element} */ (node));
}

new MutationObserver((mutations) => {
  for (const { addedNodes } of mutations) {
    for (const node of addedNodes) {
      if (isConsentNode(node)) translate(/** @type {Element} */ (node));
    }
  }
}).observe(document.body, { childList: true });

/**
 * Opens Shopify's cookie preferences dialog from the footer's "Küpsiste eelistused" button.
 * Shopify's privacyBanner API isn't always on the page (e.g. once consent has been given, or
 * when a content blocker stops its script), so if it's missing the consent feature is loaded
 * first and the dialog is opened once it's ready.
 */
function showCookiePreferences() {
  const open = () => {
    const banner = /** @type {any} */ (window).privacyBanner;
    if (!banner) return false;
    try {
      banner.showPreferences();
    } catch {
      banner.loadBanner?.({});
      banner.showPreferences?.();
    }
    return true;
  };

  if (open()) return;

  const shopify = /** @type {any} */ (window).Shopify;
  shopify?.loadFeatures?.([{ name: 'consent-tracking-api', version: '0.1' }], () => {
    if (!open()) console.warn('[cookie-banner] Shopify privacy banner API is not available');
  });
}

document.addEventListener('click', (event) => {
  const target = event.target;
  if (target instanceof Element && target.closest('[data-open-cookie-preferences]')) showCookiePreferences();
});
